import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, DollarSign, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import ScheduleC from './ScheduleC';

export default function SummaryPanel({ user }) {
  const [summary, setSummary] = useState(null);
  const [taxData, setTaxData] = useState(null);
  const [taxDeposits, setTaxDeposits] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('taxyear');
  const [selectedTaxYear, setSelectedTaxYear] = useState(new Date().getFullYear().toString());
  const [taxRate, setTaxRate] = useState(20);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showTaxPanel, setShowTaxPanel] = useState(false);
  const [showSummaryCards, setShowSummaryCards] = useState(false);
  const [showEstimatedTaxes, setShowEstimatedTaxes] = useState(false);

  // Available tax years (current year and previous years)
  const currentYear = new Date().getFullYear();
  const availableTaxYears = [
    currentYear.toString(),
    (currentYear - 1).toString(),
    (currentYear - 2).toString(),
  ];

  useEffect(() => {
    fetchSummary();
    fetchTaxDeposits();
  }, [selectedPeriod, dateRange, selectedTaxYear]);

  useEffect(() => {
    if (summary) {
      fetchTaxCalculation();
    }
  }, [summary, taxRate]);

  const getDateRange = () => {
    const now = new Date();
    let start, end;

    switch (selectedPeriod) {
      case 'day':
        start = startOfDay(now);
        end = endOfDay(now);
        break;
      case 'week':
        start = startOfWeek(now);
        end = endOfWeek(now);
        break;
      case 'month':
        start = startOfMonth(now);
        end = endOfMonth(now);
        break;
      case 'year':
        start = startOfYear(now);
        end = endOfYear(now);
        break;
      case 'taxyear':
        // Use selected tax year (Jan 1 - Dec 31 of that year)
        const year = parseInt(selectedTaxYear);
        start = new Date(year, 0, 1); // Jan 1
        end = new Date(year, 11, 31); // Dec 31
        break;
      case 'custom':
        if (dateRange.start && dateRange.end) {
          return { start: dateRange.start, end: dateRange.end };
        }
        return null;
      default:
        return null;
    }

    return {
      start: format(start, 'yyyy-MM-dd'),
      end: format(end, 'yyyy-MM-dd')
    };
  };

  const fetchSummary = async () => {
    try {
      const range = getDateRange();
      let url = `${API}/summary?period=${selectedPeriod}`;
      
      if (range) {
        url += `&start_date=${range.start}&end_date=${range.end}`;
      }

      const response = await axios.get(url);
      setSummary(response.data);
    } catch (error) {
      toast.error('Failed to fetch summary');
    }
  };

  const fetchTaxCalculation = async () => {
    try {
      const range = getDateRange();
      let url = `${API}/tax-calculation?tax_rate=${taxRate}`;
      
      if (range) {
        url += `&start_date=${range.start}&end_date=${range.end}`;
      }

      const response = await axios.get(url);
      setTaxData(response.data);
    } catch (error) {
      toast.error('Failed to calculate tax');
    }
  };

  const fetchTaxDeposits = async () => {
    try {
      const range = getDateRange();
      let url = `${API}/tax-deposits`;
      
      if (range) {
        url += `?start_date=${range.start}&end_date=${range.end}`;
      }

      const response = await axios.get(url);
      setTaxDeposits(response.data);
    } catch (error) {
      toast.error('Failed to fetch tax deposits');
    }
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    if (period !== 'custom') {
      setDateRange({ start: '', end: '' });
    }
  };

  const handleCustomDateChange = () => {
    if (dateRange.start && dateRange.end) {
      setSelectedPeriod('custom');
      fetchSummary();
    }
  };

  if (!summary) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">Loading summary...</div>
      </div>
    );
  }

  const profit = summary.profit;
  const isProfitable = profit >= 0;

  return (
    <div className="space-y-3" data-testid="summary-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white">Summary & Tax Estimation</h2>
          <p className="text-slate-400 mt-0.5 text-sm">Financial overview and tax calculations</p>
        </div>
        
        {/* Tax Year Selector */}
        <div className="flex items-center gap-3 bg-slate-800/50 border border-slate-700 rounded-lg p-2 shadow-sm">
          <Calendar className="w-5 h-5 text-cyan-400" />
          <Label className="text-sm font-medium text-slate-300">Tax Year:</Label>
          <Select value={selectedTaxYear} onValueChange={(value) => setSelectedTaxYear(value)}>
            <SelectTrigger className="w-28 h-9 border-slate-600 bg-slate-900 text-cyan-400" data-testid="tax-year-selector">
              <SelectValue placeholder="Select year" />
            </SelectTrigger>
            <SelectContent>
              {availableTaxYears.map(year => (
                <SelectItem key={year} value={year}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tax Year Info Banner */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 flex items-center gap-2">
        <Calendar className="w-4 h-4 text-indigo-600" />
        <span className="text-sm text-indigo-800">
          Showing data for <strong>Tax Year {selectedTaxYear}</strong> (Jan 1 - Dec 31, {selectedTaxYear})
        </span>
      </div>

      {/* Summary Cards */}
      <Card className="border-0 shadow">
        <CardHeader 
          className="bg-gradient-to-r from-slate-50 to-blue-50 border-b pb-1.5 pt-2.5 px-3 cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => setShowSummaryCards(!showSummaryCards)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-bold !text-slate-800">Financial Summary</CardTitle>
            {showSummaryCards ? (
              <ChevronUp className="w-5 h-5 !text-slate-600" />
            ) : (
              <ChevronDown className="w-5 h-5 !text-slate-600" />
            )}
          </div>
        </CardHeader>
        {showSummaryCards && (
          <CardContent className="pt-0 pb-3 px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
        <Card className="border-0 shadow bg-gradient-to-br from-emerald-50 to-teal-50">
          <CardHeader className="pb-1.5 pt-2.5 px-3">
            <CardDescription className="text-emerald-700 text-[10px]">Total Income</CardDescription>
            <CardTitle className="text-lg font-bold text-emerald-900 flex items-center gap-1.5" data-testid="summary-income">
              <DollarSign className="w-3.5 h-3.5" />
              ${summary.total_income.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-2.5 px-3">
            <p className="text-[10px] text-emerald-700">{summary.income_count} entries</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow bg-gradient-to-br from-red-50 to-orange-50">
          <CardHeader className="pb-1.5 pt-2.5 px-3">
            <CardDescription className="text-red-700 text-[10px]">Total Expenses</CardDescription>
            <CardTitle className="text-lg font-bold text-red-900 flex items-center gap-1.5" data-testid="summary-expenses">
              <DollarSign className="w-3.5 h-3.5" />
              ${summary.total_expenses.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-2.5 px-3">
            <p className="text-[10px] text-red-700">{summary.expense_count} entries</p>
          </CardContent>
        </Card>

        <Card className={`border-0 shadow ${isProfitable ? 'bg-gradient-to-br from-blue-50 to-indigo-50' : 'bg-gradient-to-br from-amber-50 to-yellow-50'}`}>
          <CardHeader className="pb-1.5 pt-2.5 px-3">
            <CardDescription className={`${isProfitable ? 'text-blue-700' : 'text-amber-700'} text-[10px]`}>Net Profit</CardDescription>
            <CardTitle className={`text-lg font-bold ${isProfitable ? 'text-blue-900' : 'text-amber-900'} flex items-center gap-1.5`} data-testid="summary-profit">
              {isProfitable ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
              ${Math.abs(profit).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-2.5 px-3">
            <p className={`text-[10px] ${isProfitable ? 'text-blue-700' : 'text-amber-700'}`}>
              {isProfitable ? 'Profitable' : 'Loss'}
            </p>
          </CardContent>
        </Card>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Tax Estimation */}
      <Card className="border-0 shadow">
        <CardHeader 
          className="bg-gradient-to-r from-purple-50 to-pink-50 border-b pb-1.5 pt-2.5 px-3 cursor-pointer hover:bg-purple-100 transition-colors"
          onClick={() => setShowEstimatedTaxes(!showEstimatedTaxes)}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-bold !text-slate-800">Estimated Taxes</CardTitle>
                  <CardDescription className="text-[10px] !text-slate-600">Calculate your tax liability</CardDescription>
                </div>
                {showEstimatedTaxes && (
                  <div className="flex gap-1 items-center mr-8">
                    <Label htmlFor="tax-rate" className="text-[10px] text-slate-700">Tax Rate:</Label>
                    <Input
                      id="tax-rate"
                      data-testid="tax-rate-input"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={taxRate}
                      onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                      className="w-20 h-9 text-sm"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="text-[10px] text-slate-700">%</span>
                  </div>
                )}
              </div>
            </div>
            {showEstimatedTaxes ? (
              <ChevronUp className="w-5 h-5 text-slate-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-600" />
            )}
          </div>
        </CardHeader>
        {showEstimatedTaxes && (
          <CardContent className="pt-2.5 pb-2.5 px-3">
          <div className="space-y-3">
            {taxData && (
              <div className="flex items-center justify-between gap-3 bg-gradient-to-r from-slate-50 to-purple-50 p-2.5 rounded-lg">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-slate-600">Taxable Profit:</span>
                  <span className="text-sm font-bold text-slate-900" data-testid="taxable-profit">
                    ${taxData.profit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>

                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-slate-600">Tax Rate:</span>
                  <span className="text-sm font-bold text-slate-900" data-testid="tax-rate-display">
                    {taxData.tax_rate}%
                  </span>
                </div>

                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-purple-800">Estimated Tax:</span>
                  <span className="text-sm font-bold text-purple-900" data-testid="estimated-tax">
                    ${taxData.estimated_tax.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}

            {taxData && taxData.profit <= 0 && (
              <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                <p className="text-sm text-amber-800">
                  No tax liability as there is no profit in the selected period.
                </p>
              </div>
            )}
          </div>
        </CardContent>
        )}
      </Card>

      {/* Tax Payment Tracking */}
      {taxData && taxDeposits && (
        <Card className="border-0 shadow">
          <CardHeader 
            className="bg-gradient-to-r from-green-50 to-emerald-50 border-b pb-1.5 pt-2.5 px-3 cursor-pointer hover:bg-slate-50 transition-colors"
            onClick={() => setShowTaxPanel(!showTaxPanel)}
          >
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base font-bold !text-slate-800">Tax Payment Status</CardTitle>
                <CardDescription className="text-[10px] !text-slate-600">Track tax deposits and remaining balance</CardDescription>
              </div>
              {showTaxPanel ? (
                <ChevronUp className="w-5 h-5 !text-slate-600" />
              ) : (
                <ChevronDown className="w-5 h-5 !text-slate-600" />
              )}
            </div>
          </CardHeader>
          {showTaxPanel && (
            <CardContent className="pt-2.5 pb-2.5 px-3">
              <div className="flex items-center justify-between gap-3 bg-gradient-to-r from-slate-50 to-green-50 p-2.5 rounded-lg">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-slate-600">Estimated Tax:</span>
                  <span className="text-sm font-bold text-slate-900" data-testid="tax-payment-estimated">
                    ${taxData.estimated_tax.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>

                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-emerald-600">Tax Deposits Made:</span>
                  <span className="text-sm font-bold text-emerald-900" data-testid="tax-deposits-made">
                    ${taxDeposits.total_deposits.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span className="text-[10px] text-slate-500">({taxDeposits.deposit_count} payments)</span>
                </div>

                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-slate-600">Balance Owed:</span>
                  <span className={`text-sm font-bold ${(taxData.estimated_tax - taxDeposits.total_deposits) > 0 ? 'text-red-900' : 'text-green-900'}`} data-testid="tax-balance-owed">
                    ${(taxData.estimated_tax - taxDeposits.total_deposits).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              {taxData.profit > 0 && (taxData.estimated_tax - taxDeposits.total_deposits) <= 0 && taxDeposits.total_deposits > 0 && (
                <div className="bg-green-50 border border-green-200 p-2 rounded-lg mt-2">
                  <p className="text-[10px] text-green-800">
                    ✓ Tax liability fully paid! {taxDeposits.total_deposits > taxData.estimated_tax && `You have an overpayment of $${(taxDeposits.total_deposits - taxData.estimated_tax).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.`}
                  </p>
                </div>
              )}

              {taxData.profit > 0 && (taxData.estimated_tax - taxDeposits.total_deposits) > 0 && (
                <div className="bg-amber-50 border border-amber-200 p-2 rounded-lg mt-2">
                  <p className="text-[10px] text-amber-800">
                    Note: Record tax payments as Expenses with category "Tax Deposit" to track them here.
                  </p>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Schedule C Section */}
      <div className="mt-8">
        <ScheduleC user={user} taxYear={selectedTaxYear} />
      </div>
    </div>
  );
}
